import { cva, type VariantProps } from 'class-variance-authority';

import { POPOVER_BOTTOM_COLLISION_SLACK } from '@/hen-ui/constants/popoverTheme';

export const POPOVER_ALIGNS = ['start', 'center', 'end'] as const;
export const POPOVER_SHADOWS = ['none', 'sm', 'md'] as const;
export const POPOVER_BACKDROPS = ['none', 'dim'] as const;
export const POPOVER_MOBILE_PANEL_SIDES = ['left', 'right'] as const;
export const POPOVER_COLLAPSE_BELOW = ['md', 'lg'] as const;

/** Distance from the trigger: a gap when detached, flush when attached. */
export const POPOVER_SIDE_OFFSET = { detached: 16, attached: 0 } as const;

export const POPOVER_COLLISION_PADDING = {
  bottom: -POPOVER_BOTTOM_COLLISION_SLACK,
} as const;

type CollisionSide = 'top' | 'right' | 'bottom' | 'left';
export type PopoverCollisionPadding =
  | number
  | Partial<Record<CollisionSide, number>>;

/**
 * A consumer's collision padding for the top, left and right edges, over the
 * fixed bottom slack. A consumer bottom value is ignored: it would let the
 * panel flip above the trigger, and `max-h-popover` subtracts exactly the
 * slack, so any other bottom value shrinks or collapses the panel.
 */
export function popoverCollisionPadding(
  consumer?: PopoverCollisionPadding,
): Partial<Record<CollisionSide, number>> {
  if (consumer === undefined) return { ...POPOVER_COLLISION_PADDING };
  const sides =
    typeof consumer === 'number'
      ? { top: consumer, right: consumer, left: consumer }
      : { top: consumer.top, right: consumer.right, left: consumer.left };
  const defined = Object.fromEntries(
    Object.entries(sides).filter(
      (entry): entry is [CollisionSide, number] =>
        typeof entry[1] === 'number' && Number.isFinite(entry[1]),
    ),
  );
  return { ...defined, ...POPOVER_COLLISION_PADDING };
}

/**
 * Lifts the trigger over the page dim while its panel is open. An ancestor
 * that makes its own stacking context under the dim layer (a z-index,
 * transform, opacity, or `isolate`) still dims it with everything inside.
 */
export const POPOVER_TRIGGER_ABOVE_DIM_CLASS = 'relative z-above-backdrop';

/**
 * Width of the small-screen side sheet: three quarters of the screen, like
 * the Navbar drawer, up to the Modal sheet width (500px).
 */
export const POPOVER_SHEET_WIDTH_CLASS = 'w-3/4 max-w-modal-sheet';

/**
 * The desktop panel fill on the sheet. The sheet's sticky title row paints
 * its own white, so it is overridden too.
 */
export const POPOVER_SHEET_SURFACE_CLASS =
  'bg-popover-panel [&_[data-slot=dialog-title-row]]:bg-popover-panel';

export const popoverContentVariants = cva(
  [
    'flex flex-col gap-sm',
    'bg-popover-panel rounded-md px-sm py-lg',
    'w-popover min-w-popover max-w-popover',
    'max-h-popover overflow-y-auto',
    'motion-safe:data-[state=open]:animate-popover-fade-in',
    'motion-safe:data-[state=closed]:animate-popover-fade-out',
    'motion-reduce:data-[state=closed]:invisible',
  ],
  {
    variants: {
      align: {
        start: '',
        center: '',
        end: '',
      },
      attached: {
        true: '',
        false: '',
      },
      shadow: {
        none: 'shadow-none',
        sm: 'shadow-sm',
        md: 'shadow-md',
      },
    },
    compoundVariants: [
      {
        attached: true,
        align: 'start',
        className: 'rounded-tl-none',
      },
      {
        attached: true,
        align: 'end',
        className: 'rounded-tr-none',
      },
    ],
    defaultVariants: {
      align: 'center',
      attached: false,
      shadow: 'none',
    },
  },
);

export const popoverSectionVariants = cva('flex flex-col gap-xs empty:hidden');

export type PopoverContentVariantProps = VariantProps<
  typeof popoverContentVariants
>;
export type PopoverAlign = (typeof POPOVER_ALIGNS)[number];
export type PopoverShadow = (typeof POPOVER_SHADOWS)[number];
export type PopoverBackdrop = (typeof POPOVER_BACKDROPS)[number];
export type PopoverMobilePanelSide =
  (typeof POPOVER_MOBILE_PANEL_SIDES)[number];
export type PopoverCollapseBelow = (typeof POPOVER_COLLAPSE_BELOW)[number];
